import { AUTO_STYLE, AnimationTriggerMetadata, animate, state, style, transition, trigger } from '@angular/animations';

export const SlideInOutDimension = {
  WIDTH: 'width',
  HEIGHT: 'height'
} as const;

export type SlideInOutDimensionType = (typeof SlideInOutDimension)[keyof typeof SlideInOutDimension];

export function getSlideInOutTokens(
  dimensionProp: SlideInOutDimensionType,
  dimensionValue: number | string,
  opacity: number | string
): { [key: string]: string | number } {
  return {
    width: dimensionProp === SlideInOutDimension.WIDTH ? dimensionValue : 'unset',
    height: dimensionProp === SlideInOutDimension.HEIGHT ? dimensionValue : 'unset',
    opacity
  };
}

/**
 * Slides an element in/out by altering its width or height.
 * Uses a boolean trigger which needs to be set to `true` if element should slide in.
 * @param name - trigger name
 * @param dimension - can be either `width` or `height`
 * @param delay - animation delay in secs
 * @param duration - animation duration in secs
 */
export function slideInOut(
  name: string,
  dimension: SlideInOutDimensionType,
  delay = 0,
  duration = 0.25
): AnimationTriggerMetadata {
  return trigger(name, [
    state('*', style(getSlideInOutTokens(dimension, 0, 0))),
    state('true', style(getSlideInOutTokens(dimension, AUTO_STYLE, 1))),
    transition('* <=> true', [animate(`${duration}s ${delay}s ease-out`)])
  ]);
}

/**
 * SlideIn/SlideOut (or expand/collapse) animation for ngFor / ngIf behavior.
 * @param name
 * @param dimension
 * @param delay
 * @param duration
 */
export function slideInOutEnterLeave(
  name: string,
  dimension: SlideInOutDimensionType,
  delay = 0,
  duration = 0.25
): AnimationTriggerMetadata {
  const hiddenStyle = style(getSlideInOutTokens(dimension, 0, 0));
  const visibleStyle = style(getSlideInOutTokens(dimension, AUTO_STYLE, 1));
  return trigger(name, [
    transition(':enter', [hiddenStyle, animate(`${duration}s ${delay}s ease-in`, visibleStyle)]),
    transition(':leave', [visibleStyle, animate(`${duration}s ${delay}s ease-out`, hiddenStyle)])
  ]);
}
